import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const ANCHOR_SETTLE_MS = 1500
const ANCHOR_POLL_MS = 50
const USER_TAKEOVER_EVENTS = ['wheel', 'pointerdown', 'keydown'] as const

export function useScrollToAnchorOnceLoaded(anchorId: string, isLoaded: boolean) {
  const { hash } = useLocation()
  useEffect(() => {
    const anchor = document.getElementById(anchorId)
    if (!isLoaded || hash !== `#${anchorId}` || !anchor) return
    return keepAnchorInView(anchor)
  }, [anchorId, isLoaded, hash])
}

// After in-app navigation the other cards are still rendering, so the first scroll falls short or
// gets pushed away. Re-align while the page settles, and yield as soon as the user scrolls, clicks or
// types. A timer, not animation frames: those never fire in a background tab, e.g. after a ctrl+click.
function keepAnchorInView(anchor: HTMLElement): () => void {
  const align = () => scrollAnchorToTop(anchor)
  align()
  const poll = setInterval(align, ANCHOR_POLL_MS)
  const deadline = setTimeout(stop, ANCHOR_SETTLE_MS)
  USER_TAKEOVER_EVENTS.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }))

  function stop() {
    clearInterval(poll)
    clearTimeout(deadline)
    USER_TAKEOVER_EVENTS.forEach((type) => window.removeEventListener(type, stop, { capture: true }))
  }

  return stop
}

// Vertical only: scrollIntoView also scrolls sideways, and while the settings tab slides in from the
// right it dragged the layout's overflow-x: hidden container along, stranding it scrolled left.
function scrollAnchorToTop(anchor: HTMLElement): void {
  const scroller = findVerticalScroller(anchor)
  const scrollerTop = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top
  const margin = parseFloat(getComputedStyle(anchor).scrollMarginTop) || 0
  scroller.scrollTo({ top: scroller.scrollTop + anchor.getBoundingClientRect().top - scrollerTop - margin })
}

function findVerticalScroller(element: HTMLElement): Element {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent)
    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) return parent
  }
  return document.scrollingElement ?? document.documentElement
}
